import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  TooltipProvider,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@/components/ui/tooltip';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  Tooltip as RechartsTooltip,
} from 'recharts';
import {
  RefreshCw, Inbox, Filter, Eye, Shield, Clock, Cpu,
  TrendingDown, Tag, Flame, Signal, HelpCircle,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useAnalyticsHints } from '@/hooks/useAnalyticsHints';
import { WeeklyBriefing } from '@/components/dashboard/WeeklyBriefing';
import { FeedChat } from '@/components/dashboard/FeedChat';
import { RuleSuggestions } from '@/components/dashboard/RuleSuggestions';

// ── Types ─────────────────────────────────────────────────────────────────────

interface FeedStats {
  total: number;
  curated: number;
  fenced: number;
  pending: number;
  analyzed: number;
  filteredRate: number;
  signalRate: number;
  uniqueRuleTags: number;
  streak: number;
  byPlatform: { platform: string; count: number; signalPct: number }[];
  byDay: { date: string; total: number; fenced: number; curated: number }[];
  topTags: { tag: string; count: number }[];
  toxicityBuckets: { label: string; count: number }[];
  avgToxicity: number | null;
}

interface LifetimeStats {
  total: number;
  memberSince: string | null;
}

const EMPTY: FeedStats = {
  total: 0, curated: 0, fenced: 0, pending: 0,
  analyzed: 0, filteredRate: 0, signalRate: 0, uniqueRuleTags: 0, streak: 0,
  byPlatform: [], byDay: [], topTags: [], toxicityBuckets: [], avgToxicity: null,
};

const PLATFORM_COLORS: Record<string, string> = {
  reddit:  '#FF4500',
  bluesky: '#0085FF',
  youtube: '#FF0000',
  twitter: '#000000',
  x:       '#000000',
};
const DEFAULT_COLOR = '#1A2E4A';

const DATE_RANGES = [
  { value: '7d',   label: 'Last 7 days'  },
  { value: '30d',  label: 'Last 30 days' },
  { value: '90d',  label: 'Last 90 days' },
  { value: '365d', label: 'Last year'    },
  { value: 'all',  label: 'All time'     },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function startOf(range: string): string | null {
  if (range === 'all') return null;
  const days = range === '7d' ? 7 : range === '30d' ? 30 : range === '90d' ? 90 : 365;
  return new Date(Date.now() - days * 86_400_000).toISOString();
}

function dayCount(range: string): number {
  if (range === '7d')   return 7;
  if (range === '30d')  return 30;
  if (range === '90d')  return 90;
  if (range === '365d') return 365;
  return 365; // 'all' — byDay chart shows last 365 days max
}

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function fmtSince(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

// ── Compute streak from byDay ─────────────────────────────────────────────────
function computeStreak(byDay: { date: string; total: number }[]): number {
  // byDay is already sorted oldest→newest. Walk backwards from today.
  let streak = 0;
  for (let i = byDay.length - 1; i >= 0; i--) {
    if (byDay[i].total > 0) streak++;
    else break;
  }
  return streak;
}

// ── Hint text ────────────────────────────────────────────────────────────────

const HINTS = {
  posts_fetched:    'Total posts SocialScoop retrieved from your connected platforms in this period.',
  in_feed:          'Posts that passed all your listening rules and appear in your main feed.',
  fenced:           'Posts your rules flagged as noise — moved aside quietly. They\'re never deleted and always retrievable from your Vault.',
  pending_review:   'Posts soft-fenced by heuristics (e.g. new accounts, low followers) during this period. Your full all-time queue — including hard-fenced posts — lives in Feeds → Review Queue.',
  ai_analyzed:      'Posts sent to AI for deeper analysis, like toxicity scoring. Requires AI rules to be active.',
  signal_rate:      '% of fetched posts that made it into your feed. Higher is better — it means your rules are working well.',
  avg_toxicity:     'Average toxicity score across AI-analyzed posts, on a 0–10 scale. 0 = perfectly clean; 10 = highly toxic. Powered by Anthropic AI.',
  active_rule_tags: 'Number of distinct rule categories that matched content in this period. More tags = broader filtering coverage.',
  streak:           'Consecutive days SocialScoop found content for your feed. Breaks if no posts were fetched on a given day.',
  lifetime_total:   'Every post SocialScoop has ever fetched across all your platforms, all time.',
  toxicity_section: 'AI rates each post on a 0–10 toxicity scale based on language, hostility, and harmful patterns. Only posts processed by your AI rules receive a score.',
  platform_signal:  '% of posts from this platform that passed your rules and made it into your feed.',
} as const;

// ── MetricHint component ──────────────────────────────────────────────────────

const MetricHint: React.FC<{ text: string; hintsEnabled: boolean }> = ({ text, hintsEnabled }) => {
  if (!hintsEnabled) return null;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <HelpCircle className="h-3 w-3 text-muted-foreground/40 hover:text-muted-foreground cursor-help ml-1 shrink-0 inline-block align-middle" />
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[210px] text-xs leading-relaxed">
        {text}
      </TooltipContent>
    </Tooltip>
  );
};

// ── Component ─────────────────────────────────────────────────────────────────

const DashboardAnalytics: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { hintsEnabled } = useAnalyticsHints();
  const [range, setRange] = useState('30d');
  const [stats, setStats] = useState<FeedStats>(EMPTY);
  const [lifetime, setLifetime] = useState<LifetimeStats>({ total: 0, memberSince: null });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);

    const since = startOf(range);

    // ── Lifetime count (separate lightweight query — no row fetch) ────────────
    const { count: lifetimeCount } = await supabase
      .from('social_feed_items')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', user.id);

    // ── Member-since date from profile ────────────────────────────────────────
    const { data: profile } = await supabase
      .from('profiles')
      .select('created_at')
      .eq('id', user.id)
      .maybeSingle();

    setLifetime({
      total: lifetimeCount ?? 0,
      memberSince: profile?.created_at ?? null,
    });

    // ── Main data fetch (50k limit — safe for current user volumes) ───────────
    let query = supabase
      .from('social_feed_items')
      .select('platform, is_filtered, is_pending_review, toxicity_score, rule_tags, fetched_at')
      .eq('user_id', user.id)
      .limit(50000);

    if (since) query = query.gte('fetched_at', since);

    const { data: items, error } = await query;

    if (error || !items) {
      setLoading(false);
      return;
    }

    // ── Totals ────────────────────────────────────────────────────────────────
    const total        = items.length;
    const fenced       = items.filter(i => i.is_filtered).length;
    const pending      = items.filter(i => i.is_pending_review && !i.is_filtered).length;
    const curated      = total - fenced - pending;
    const analyzed     = items.filter(i => i.toxicity_score !== null && i.toxicity_score !== undefined).length;
    const filteredRate = total > 0 ? Math.round((fenced  / total) * 100) : 0;
    const signalRate   = total > 0 ? Math.round((curated / total) * 100) : 0;

    // ── By platform (with per-platform signal %) ──────────────────────────────
    const platMap = new Map<string, { total: number; curated: number }>();
    for (const i of items) {
      const p = platMap.get(i.platform) ?? { total: 0, curated: 0 };
      p.total++;
      if (!i.is_filtered && !i.is_pending_review) p.curated++;
      platMap.set(i.platform, p);
    }
    const byPlatform = [...platMap.entries()]
      .sort((a, b) => b[1].total - a[1].total)
      .map(([platform, v]) => ({
        platform,
        count: v.total,
        signalPct: v.total > 0 ? Math.round((v.curated / v.total) * 100) : 0,
      }));

    // ── By day (chart shows up to 365 days for 'all' range) ──────────────────
    const days = dayCount(range);
    const dayMap = new Map<string, { total: number; fenced: number; curated: number }>();
    for (let d = days - 1; d >= 0; d--) {
      const dt = new Date(Date.now() - d * 86_400_000);
      dayMap.set(dt.toISOString().slice(0, 10), { total: 0, fenced: 0, curated: 0 });
    }
    for (const i of items) {
      const key = (i.fetched_at ?? '').slice(0, 10);
      if (!dayMap.has(key)) continue;
      const bucket = dayMap.get(key)!;
      bucket.total++;
      if (i.is_filtered) bucket.fenced++;
      else bucket.curated++;
    }
    const byDay = [...dayMap.entries()].map(([date, v]) => ({ date: fmtDate(date), ...v }));

    // ── Streak ────────────────────────────────────────────────────────────────
    const rawDays = [...dayMap.entries()].map(([date, v]) => ({ date, total: v.total }));
    const streak = computeStreak(rawDays);

    // ── Top rule tags ─────────────────────────────────────────────────────────
    const tagMap = new Map<string, number>();
    for (const i of items) {
      const tags: string[] = Array.isArray(i.rule_tags) ? i.rule_tags : [];
      for (const t of tags) tagMap.set(t, (tagMap.get(t) ?? 0) + 1);
    }
    const topTags = [...tagMap.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([tag, count]) => ({ tag, count }));
    const uniqueRuleTags = tagMap.size;

    // ── Toxicity distribution ─────────────────────────────────────────────────
    const scored = items.filter(i => i.toxicity_score !== null && i.toxicity_score !== undefined);
    const buckets = [
      { label: '0–2  Clean',    min: 0,   max: 0.2  },
      { label: '2–4',           min: 0.2, max: 0.4  },
      { label: '4–6  Moderate', min: 0.4, max: 0.6  },
      { label: '6–8  Elevated', min: 0.6, max: 0.8  },
      { label: '8–10 High',     min: 0.8, max: 1.01 },
    ];
    const toxicityBuckets = buckets.map(b => ({
      label: b.label,
      count: scored.filter(i => i.toxicity_score! >= b.min && i.toxicity_score! < b.max).length,
    }));
    const avgToxicity = scored.length
      ? scored.reduce((s, i) => s + i.toxicity_score!, 0) / scored.length
      : null;

    setStats({
      total, curated, fenced, pending, analyzed,
      filteredRate, signalRate, uniqueRuleTags, streak,
      byPlatform, byDay, topTags, toxicityBuckets, avgToxicity,
    });
    setLoading(false);
  }, [user, range]);

  useEffect(() => { load(); }, [load]);

  const PIE_DATA = [
    { name: 'In your feed',   value: stats.curated, color: '#1A2E4A' },
    { name: 'Fenced',         value: stats.fenced,  color: '#E5E7EB' },
    { name: 'Pending review', value: stats.pending, color: '#F59E0B' },
  ].filter(d => d.value > 0);

  const rangeLabel = DATE_RANGES.find(r => r.value === range)?.label ?? range;

  // Decide X-axis tick interval for the bar chart to avoid crowding
  const tickInterval = range === '7d' ? 0 : range === '30d' ? 4 : range === '90d' ? 13 : 30;

  return (
    <TooltipProvider delayDuration={200}>
    <div className="space-y-6 p-6">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#1A2E4A]">Analytics</h1>
          <p className="text-sm text-muted-foreground mt-0.5">What your feed has been doing</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={range} onValueChange={setRange}>
            <SelectTrigger className="w-36 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DATE_RANGES.map(r => (
                <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* ── Lifetime banner ─────────────────────────────────────────────────── */}
      <div className="rounded-xl border border-[#E5E7EB] bg-[#1A2E4A]/[0.03] px-5 py-4 flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-1 flex items-center">
            Lifetime posts processed
            <MetricHint text={HINTS.lifetime_total} hintsEnabled={hintsEnabled} />
          </p>
          <p className="text-3xl font-bold text-[#1A2E4A]">
            {loading ? '—' : lifetime.total.toLocaleString()}
          </p>
          {lifetime.memberSince && (
            <p className="text-xs text-muted-foreground mt-0.5">
              Member since {fmtSince(lifetime.memberSince)}
            </p>
          )}
        </div>
        <div className="flex items-center gap-6">
          <div className="text-right">
            <p className="text-xs text-muted-foreground mb-0.5 flex items-center justify-end gap-0.5">
              Active streak
              <MetricHint text={HINTS.streak} hintsEnabled={hintsEnabled} />
            </p>
            <p className="text-xl font-bold text-[#1A2E4A] flex items-center gap-1.5 justify-end">
              <Flame className="h-4 w-4 text-orange-500" />
              {loading ? '—' : `${stats.streak}d`}
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs text-muted-foreground mb-0.5 flex items-center justify-end gap-0.5">
              Signal rate
              <MetricHint text={HINTS.signal_rate} hintsEnabled={hintsEnabled} />
            </p>
            <p className={`text-xl font-bold flex items-center gap-1.5 justify-end ${
              stats.signalRate >= 70 ? 'text-green-700' :
              stats.signalRate >= 40 ? 'text-[#1A2E4A]' : 'text-amber-600'
            }`}>
              <Signal className="h-4 w-4" />
              {loading ? '—' : `${stats.signalRate}%`}
            </p>
          </div>
        </div>
      </div>

      {/* ── Summary cards row 1 ─────────────────────────────────────────────── */}
      <div>
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">
          {rangeLabel}
        </p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'Posts fetched',  value: stats.total,   icon: Inbox,  color: 'text-[#1A2E4A]', hint: HINTS.posts_fetched,  link: null },
            { label: 'In your feed',   value: stats.curated, icon: Eye,    color: 'text-green-700',  hint: HINTS.in_feed,         link: null },
            { label: 'Fenced',         value: stats.fenced,  icon: Filter, color: 'text-gray-400',   hint: HINTS.fenced,          link: '/dashboard/feeds?tab=review' },
            { label: 'Pending review', value: stats.pending, icon: Clock,  color: 'text-amber-600',  hint: HINTS.pending_review,  link: '/dashboard/feeds?tab=review' },
          ].map(({ label, value, icon: Icon, color, hint, link }) => {
            const inner = (
              <CardContent className="p-5 flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground flex items-center">
                    {label}
                    <MetricHint text={hint} hintsEnabled={hintsEnabled} />
                  </p>
                  <p className={`text-2xl font-bold mt-0.5 ${color}`}>
                    {loading ? '—' : value.toLocaleString()}
                  </p>
                  {link && !loading && value > 0 && (
                    <p className="text-[10px] text-muted-foreground mt-0.5 underline underline-offset-2">
                      View in queue →
                    </p>
                  )}
                </div>
                <Icon className={`h-6 w-6 ${color} opacity-50`} />
              </CardContent>
            );
            return (
              <Card key={label} className={`border border-[#E5E7EB] ${link && !loading && value > 0 ? 'hover:border-amber-300 transition-colors' : ''}`}>
                {link && !loading && value > 0
                  ? <Link to={link} className="block">{inner}</Link>
                  : inner
                }
              </Card>
            );
          })}
        </div>
      </div>

      {/* ── Content insight cards row 2 ─────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          {
            label: 'AI analyzed',
            value: stats.analyzed,
            icon: Cpu,
            color: 'text-blue-600',
            fmt: (v: number) => v.toLocaleString(),
            sub: stats.total > 0 ? `${Math.round((stats.analyzed / stats.total) * 100)}% of posts` : undefined,
            hint: HINTS.ai_analyzed,
          },
          {
            label: 'Signal rate',
            value: stats.signalRate,
            icon: Signal,
            color: stats.signalRate >= 70 ? 'text-green-700' : stats.signalRate >= 40 ? 'text-[#1A2E4A]' : 'text-amber-600',
            fmt: (v: number) => `${v}%`,
            sub: 'posts reaching your feed',
            hint: HINTS.signal_rate,
          },
          {
            label: 'Avg toxicity',
            value: stats.avgToxicity !== null ? parseFloat((stats.avgToxicity * 10).toFixed(1)) : null,
            icon: TrendingDown,
            color: stats.avgToxicity !== null && stats.avgToxicity * 10 >= 6 ? 'text-red-600'
                 : stats.avgToxicity !== null && stats.avgToxicity * 10 >= 3 ? 'text-amber-600'
                 : 'text-green-700',
            fmt: (v: number) => `${v}/10`,
            sub: stats.analyzed > 0 ? `across ${stats.analyzed.toLocaleString()} scored posts` : undefined,
            empty: 'No AI scores yet',
            hint: HINTS.avg_toxicity,
          },
          {
            label: 'Active rule tags',
            value: stats.uniqueRuleTags,
            icon: Tag,
            color: 'text-[#1A2E4A]',
            fmt: (v: number) => v.toLocaleString(),
            sub: stats.topTags[0] ? `top: ${stats.topTags[0].tag}` : undefined,
            hint: HINTS.active_rule_tags,
          },
        ].map(({ label, value, icon: Icon, color, fmt, sub, empty, hint }) => (
          <Card key={label} className="border border-[#E5E7EB]">
            <CardContent className="p-5 flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground flex items-center">
                  {label}
                  <MetricHint text={hint} hintsEnabled={hintsEnabled} />
                </p>
                <p className={`text-2xl font-bold mt-0.5 ${color}`}>
                  {loading ? '—' : value === null ? (empty ?? '—') : fmt(value as number)}
                </p>
                {!loading && sub && (
                  <p className="text-[11px] text-muted-foreground mt-0.5 leading-tight">{sub}</p>
                )}
              </div>
              <Icon className={`h-6 w-6 ${color} opacity-50`} />
            </CardContent>
          </Card>
        ))}
      </div>

      {/* ── Activity over time chart ─────────────────────────────────────────── */}
      <Card className="border border-[#E5E7EB]">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-base font-semibold text-[#1A2E4A]">Posts per day</CardTitle>
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-3 h-3 rounded-sm bg-[#1A2E4A]" />
                In feed
              </span>
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-3 h-3 rounded-sm bg-[#D1D5DB]" />
                Fenced
              </span>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="h-52 flex items-center justify-center text-muted-foreground text-sm">Loading…</div>
          ) : stats.byDay.every(d => d.total === 0) ? (
            <div className="h-52 flex items-center justify-center text-muted-foreground text-sm">No activity in this period</div>
          ) : (
            <ResponsiveContainer width="100%" height={210}>
              <BarChart data={stats.byDay} barGap={2}>
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                  interval={tickInterval}
                />
                <YAxis
                  tick={{ fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                  allowDecimals={false}
                />
                <RechartsTooltip
                  contentStyle={{ fontSize: 12, border: '1px solid #E5E7EB', borderRadius: 8 }}
                  cursor={{ fill: '#F9FAFB' }}
                />
                <Bar dataKey="curated" name="In feed" fill="#1A2E4A" radius={[3, 3, 0, 0]} stackId="a" />
                <Bar dataKey="fenced"  name="Fenced"  fill="#D1D5DB" radius={[3, 3, 0, 0]} stackId="a" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* ── Platform + composition + toxicity ─────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Platform breakdown with signal % */}
        <Card className="border border-[#E5E7EB]">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-[#1A2E4A] flex items-center">
              By platform
              <MetricHint text={HINTS.platform_signal} hintsEnabled={hintsEnabled} />
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="h-32 flex items-center justify-center text-sm text-muted-foreground">Loading…</div>
            ) : stats.byPlatform.length === 0 ? (
              <p className="text-sm text-muted-foreground">No posts fetched yet</p>
            ) : (
              <div className="space-y-4 mt-1">
                {stats.byPlatform.map(({ platform, count, signalPct }) => (
                  <div key={platform}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="capitalize font-medium">{platform}</span>
                      <span className="flex items-center gap-2 text-muted-foreground">
                        <span
                          className="text-[11px] font-medium px-1.5 py-0.5 rounded"
                          style={{
                            backgroundColor: signalPct >= 70 ? '#DCFCE7' : signalPct >= 40 ? '#F3F4F6' : '#FEF3C7',
                            color:           signalPct >= 70 ? '#166534' : signalPct >= 40 ? '#374151' : '#92400E',
                          }}
                        >
                          {signalPct}% signal
                        </span>
                        {count.toLocaleString()}
                      </span>
                    </div>
                    <div className="w-full bg-[#F3F4F6] rounded-full h-1.5">
                      <div
                        className="h-1.5 rounded-full"
                        style={{
                          width: `${Math.round((count / stats.total) * 100)}%`,
                          backgroundColor: PLATFORM_COLORS[platform] ?? DEFAULT_COLOR,
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Feed composition donut */}
        <Card className="border border-[#E5E7EB]">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-[#1A2E4A]">Feed composition</CardTitle>
          </CardHeader>
          <CardContent className="flex items-center justify-center">
            {loading || stats.total === 0 ? (
              <div className="h-32 flex items-center justify-center text-sm text-muted-foreground">
                {loading ? 'Loading…' : 'No data yet'}
              </div>
            ) : (
              <PieChart width={200} height={175}>
                <Pie data={PIE_DATA} dataKey="value" cx="50%" cy="45%" outerRadius={62} innerRadius={38}>
                  {PIE_DATA.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                <RechartsTooltip contentStyle={{ fontSize: 12, border: '1px solid #E5E7EB', borderRadius: 8 }} />
              </PieChart>
            )}
          </CardContent>
        </Card>

        {/* Toxicity distribution */}
        <Card className="border border-[#E5E7EB]">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold text-[#1A2E4A] flex items-center">
                Toxicity scores
                <MetricHint text={HINTS.toxicity_section} hintsEnabled={hintsEnabled} />
              </CardTitle>
              {stats.avgToxicity !== null && !loading && (
                <Badge variant="outline" className="text-xs font-normal">
                  avg {(stats.avgToxicity * 10).toFixed(1)}/10
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="h-32 flex items-center justify-center text-sm text-muted-foreground">Loading…</div>
            ) : stats.toxicityBuckets.every(b => b.count === 0) ? (
              <p className="text-sm text-muted-foreground">AI scoring hasn't run yet for this period</p>
            ) : (
              <div className="space-y-2.5 mt-1">
                {stats.toxicityBuckets.map(({ label, count }) => {
                  const isHigh     = label.includes('High');
                  const isElevated = label.includes('Elevated');
                  const barColor   = isHigh ? '#EF4444' : isElevated ? '#F59E0B' : '#1A2E4A';
                  const barOpacity = isHigh ? 1 : isElevated ? 0.8 : 0.35;
                  return (
                    <div key={label}>
                      <div className="flex justify-between text-xs mb-0.5">
                        <span className="text-muted-foreground">{label}</span>
                        <span className="font-medium tabular-nums">{count.toLocaleString()}</span>
                      </div>
                      <div className="w-full bg-[#F3F4F6] rounded-full h-1.5">
                        <div
                          className="h-1.5 rounded-full transition-all"
                          style={{
                            width: stats.analyzed > 0 ? `${Math.round((count / stats.analyzed) * 100)}%` : '0%',
                            backgroundColor: barColor,
                            opacity: barOpacity,
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ── Top rule tags ────────────────────────────────────────────────────── */}
      {!loading && stats.topTags.length > 0 && (
        <Card className="border border-[#E5E7EB]">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-[#1A2E4A]">Most active rule tags</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {stats.topTags.map(({ tag, count }) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => navigate(`/dashboard/feeds?tab=by-rule&rule=${encodeURIComponent(tag)}`)}
                  className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-normal transition-colors hover:bg-muted hover:border-[#1A2E4A]/40 cursor-pointer"
                  title={`See posts tagged: ${tag}`}
                >
                  {tag}
                  <span className="ml-1.5 text-muted-foreground">{count.toLocaleString()}</span>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Empty state ──────────────────────────────────────────────────────── */}
      {!loading && stats.total === 0 && (
        <div className="text-center py-16 text-muted-foreground text-sm">
          <Shield className="h-8 w-8 mx-auto mb-3 opacity-25" />
          <p className="font-medium">No feed items in this date range yet.</p>
          <p className="mt-1">Content is fetched every 20 minutes once platforms are connected.</p>
        </div>
      )}

      {/* ── AI Features ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <WeeklyBriefing />
        <FeedChat />
      </div>
      <RuleSuggestions />
    </div>
    </TooltipProvider>
  );
};

export default DashboardAnalytics;
